import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Alert,
  IconButton,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  CloudDone as CloudDoneIcon,
  CloudOff as CloudOffIcon,
  Refresh as RefreshIcon,
  Key as KeyIcon,
  Storage as StorageIcon,
  Speed as SpeedIcon,
  Sync as SyncIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  WarningAmber as WarningIcon,
  FormatListBulleted as ListIcon
} from '@mui/icons-material';
import { checkServerHealth, testGoogleSheetsAuth } from '../services/serverService';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

// Define types for our server status data
interface ServiceStatus {
  name: string;
  status: 'online' | 'offline' | 'warning' | 'loading';
  lastChecked: string;
  details?: string;
}

const ServerStatus: React.FC = () => {
  const [services, setServices] = useState<ServiceStatus[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<string>('');
  const { currentUser, userData } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // On mount, check if user is admin, if not redirect
  useEffect(() => {
    if (userData?.role !== 'admin') {
      navigate('/dashboard');
    }
  }, [userData, navigate]);

  // Check server status on component mount
  useEffect(() => {
    checkServerStatus();
  }, []);

  // Function to check all server services
  const checkServerStatus = async () => {
    setIsLoading(true);
    setError(null);
    const currentTime = new Date().toLocaleString();
    setLastRefresh(currentTime);

    try {
      // Create an array to store all service statuses
      const updatedServices: ServiceStatus[] = [];

      // Check the main API server health
      try {
        const healthResult = await checkServerHealth();
        updatedServices.push({
          name: 'API Server',
          status: healthResult.status === 'ok' ? 'online' : 'offline',
          lastChecked: currentTime,
          details: healthResult.message || ''
        });
      } catch (err) {
        updatedServices.push({
          name: 'API Server',
          status: 'offline',
          lastChecked: currentTime,
          details: 'Connection error'
        });
      }

      // Check Google Sheets integration
      try {
        const sheetsResult = await testGoogleSheetsAuth();
        updatedServices.push({
          name: 'Google Sheets Integration',
          status: sheetsResult.success ? 'online' : 'warning',
          lastChecked: currentTime,
          details: sheetsResult.message || ''
        });
      } catch (err) {
        updatedServices.push({
          name: 'Google Sheets Integration',
          status: 'offline',
          lastChecked: currentTime,
          details: 'Connection error'
        });
      }

      // Add Firebase server status (we'll just assume it's online if we can load this page)
      updatedServices.push({
        name: 'Firebase Hosting',
        status: 'online',
        lastChecked: currentTime,
        details: 'Connected'
      });

      // Add API Key status (we check this by the result of the API Server check)
      updatedServices.push({
        name: 'API Key Validation',
        status: updatedServices[0].status === 'online' ? 'online' : 'warning',
        lastChecked: currentTime,
        details: updatedServices[0].status === 'online' 
          ? 'API key is valid' 
          : 'Could not validate API key'
      });

      setServices(updatedServices);
    } catch (err) {
      setError('Failed to check server status. Please try again.');
      console.error('Server status check error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Get status icon based on status
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'online':
        return <CheckCircleIcon color="success" />;
      case 'offline':
        return <ErrorIcon color="error" />;
      case 'warning':
        return <WarningIcon color="warning" />;
      default:
        return <CircularProgress size={20} />;
    }
  };

  // Get status chip color
  const getStatusChip = (status: 'online' | 'offline' | 'warning' | 'loading') => {
    let color: 'success' | 'error' | 'warning' | 'default' = 'default';
    let label = 'Unknown';

    switch (status) {
      case 'online':
        color = 'success';
        label = 'Online';
        break;
      case 'offline':
        color = 'error';
        label = 'Offline';
        break;
      case 'warning':
        color = 'warning';
        label = 'Warning';
        break;
      case 'loading':
        label = 'Checking...';
        break;
    }

    return <Chip label={label} color={color} size={isMobile ? 'small' : 'medium'} />;
  };

  // Calculate overall system status
  const getOverallStatus = (): 'online' | 'offline' | 'warning' => {
    if (services.some(service => service.status === 'offline')) {
      return 'offline';
    } else if (services.some(service => service.status === 'warning')) {
      return 'warning';
    } else {
      return 'online';
    }
  };

  if (userData?.role !== 'admin') {
    return (
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Alert severity="error">
          You don't have permission to access this page.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper
        elevation={3}
        sx={{
          p: { xs: 2, md: 4 },
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Server Status
          </Typography>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={checkServerStatus}
            disabled={isLoading}
          >
            Refresh
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* System Status Summary */}
        <Card
          elevation={2}
          sx={{
            mb: 4,
            bgcolor: getOverallStatus() === 'online'
              ? 'success.light'
              : getOverallStatus() === 'warning'
                ? 'warning.light'
                : 'error.light',
            color: getOverallStatus() === 'online'
              ? 'success.contrastText'
              : getOverallStatus() === 'warning'
                ? 'warning.contrastText'
                : 'error.contrastText'
          }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {getOverallStatus() === 'online' ? (
                <CloudDoneIcon fontSize="large" sx={{ mr: 2 }} />
              ) : getOverallStatus() === 'warning' ? (
                <WarningIcon fontSize="large" sx={{ mr: 2 }} />
              ) : (
                <CloudOffIcon fontSize="large" sx={{ mr: 2 }} />
              )}
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  System Status: {getOverallStatus() === 'online' ? 'All Systems Operational' : getOverallStatus() === 'warning' ? 'Some Issues Detected' : 'System Outage'}
                </Typography>
                <Typography variant="body2">
                  Last checked: {lastRefresh}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Service Status List */}
        <Divider sx={{ mb: 3 }} />
        <Typography variant="h6" gutterBottom>
          Service Status
        </Typography>

        <Grid container spacing={3}>
          {isLoading ? (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            </Grid>
          ) : (
            services.map((service, index) => (
              <Grid item xs={12} sm={6} md={6} key={index}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                        {service.name === 'API Server' && <SpeedIcon sx={{ mr: 1 }} />}
                        {service.name === 'Google Sheets Integration' && <StorageIcon sx={{ mr: 1 }} />}
                        {service.name === 'Firebase Hosting' && <SyncIcon sx={{ mr: 1 }} />}
                        {service.name === 'API Key Validation' && <KeyIcon sx={{ mr: 1 }} />}
                        {service.name}
                      </Typography>
                      {getStatusChip(service.status)}
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {service.details}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))
          )}
        </Grid>

        {/* Server Information */}
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" gutterBottom>
          Server Information
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                  API Server Details
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon><SettingsIcon /></ListItemIcon>
                    <ListItemText
                      primary="Server URL"
                      secondary="https://api-6ul3istoeq-uc.a.run.app"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><ListIcon /></ListItemIcon>
                    <ListItemText 
                      primary="Runtime" 
                      secondary="Node.js 18 (Firebase Functions)" 
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                  API Key Information
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon><KeyIcon /></ListItemIcon>
                    <ListItemText 
                      primary="Default API Key" 
                      secondary="sk_5eb6ee...6f36f97" 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><SettingsIcon /></ListItemIcon>
                    <ListItemText 
                      primary="Manage API Keys" 
                      secondary={
                        <Button 
                          size="small" 
                          href="/sheets-test" 
                          target="_blank" 
                          variant="outlined" 
                          sx={{ mt: 1 }}
                        >
                          Test API Integration
                        </Button>
                      } 
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Troubleshooting Guide */}
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" gutterBottom>
          Troubleshooting
        </Typography>

        <Card variant="outlined" sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
              If the server is offline:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><RefreshIcon /></ListItemIcon>
                <ListItemText 
                  primary="Restart the server" 
                  secondary="Use the Firebase Console or run 'firebase deploy --only functions:api'" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><KeyIcon /></ListItemIcon>
                <ListItemText 
                  primary="Check API Key" 
                  secondary="Ensure the API key is valid and properly configured" 
                />
              </ListItem>
            </List>
          </CardContent>
        </Card>
      </Paper>
    </Container>
  );
};

export default ServerStatus; 